// ============================================================================
// TICKET SERVICE (Supabase)
// ============================================================================

import { supabase } from './auth.service';
import { invokeEdgeFunction } from '../lib/edge-function';
import { PopulatedTicket, Ticket, TicketContent, TicketReply } from '../entities';
import type { TicketGroup } from '../entities/ticket-group.entity';
import type { Channel } from '../entities/channel.entity';
import type { Order } from '../entities/order.entity';

export interface GenerateAiReplyResponse {
  replies: string[];
}

export interface AnalyzeSentimentResponse {
  sentiment: 'POSITIVE' | 'NEGATIVE' | 'NEUTRAL';
  confidence: number;
}

export interface TicketCountsResponse {
  statuses: Record<string, number>;
  priorities: Record<string, number>;
  aiTags: Record<string, number>;
  queues: Record<string, number>;
  assignments: {
    assigned: number;
    unassigned: number;
  };
}

export class TicketService {
  private static readonly TICKET_SELECT =
    '*, channels(*), users!left(*), orders!order_id(*), social_users:social_user_id(id, account_id, channel_id, network_id, network, full_name, username, profile_picture)';

  constructor() {}

  async loadAll(filters?: {
    status?: string;
    statuses?: string[];
    network?: string;
    type?: string;
    sortOrder?: string;
    aiTags?: string[];
    priorities?: string[];
    sentiments?: string[];
    search?: string;
    channelId?: string;
    startDate?: string;
    endDate?: string;
    productId?: string;
    queue?: string;
    assignedTo?: string;
    excludeTicketIds?: string[];
  }): Promise<PopulatedTicket[]> {
    const needsChannelJoin = filters?.network && filters.network !== 'all';
    const selectStr = needsChannelJoin
      ? '*, channels!inner(*), users!left(*), orders!order_id(*), social_users:social_user_id(id, account_id, channel_id, network_id, network, full_name, username, profile_picture)'
      : TicketService.TICKET_SELECT;

    let query = supabase
      .from('tickets')
      .select(selectStr)
      .eq('is_meta_ticket', false)
      .eq('is_own', false);

    // Status filter
    if (filters?.status) {
      query = query.eq('status', filters.status);
    } else if (filters?.statuses && filters.statuses.length > 0) {
      query = query.in('status', filters.statuses);
    } else {
      query = query.neq('status', 'CLOSED');
    }

    // Network filter (uses !inner join to filter parent tickets)
    if (needsChannelJoin) {
      query = query.eq('channels.network', filters!.network);
    }

    // Type filter
    if (filters?.type && filters.type !== 'all') {
      query = query.eq('type', filters.type);
    }

    // Priority filter
    if (filters?.priorities && filters.priorities.length > 0) {
      query = query.in('priority', filters.priorities);
    }

    // Sentiment filter
    if (filters?.sentiments && filters.sentiments.length > 0) {
      query = query.in('sentiment', filters.sentiments);
    }

    // AI tags filter (overlap check - contains any of the specified tags)
    if (filters?.aiTags && filters.aiTags.length > 0) {
      query = query.contains('tags', filters.aiTags);
    }

    // Channel filter
    if (filters?.channelId) {
      query = query.eq('channel_id', filters.channelId);
    }

    // Assigned to filter
    if (filters?.assignedTo === 'null' || filters?.assignedTo === '') {
      query = query.is('assigned_to', null);
    } else if (filters?.assignedTo) {
      query = query.eq('assigned_to', filters.assignedTo);
    }

    // Queue filter (check if ticket is in a specific queue - this may require folder lookup)
    // For now, skip queue filtering as it requires folder relationship

    // Date range filter
    if (filters?.startDate) {
      query = query.gte('created_at', filters.startDate);
    }
    if (filters?.endDate) {
      query = query.lte('created_at', filters.endDate);
    }

    // Search filter
    if (filters?.search && filters.search.trim()) {
      query = query.or(`message.ilike.%${filters.search}%,content.ilike.%${filters.search}%`);
    }

    // Sort order
    if (filters?.sortOrder === 'asc' || filters?.sortOrder === 'desc') {
      query = query
        .order('created_at_network', { ascending: filters.sortOrder === 'asc', nullsFirst: false })
        .order('created_at', { ascending: filters.sortOrder === 'asc' });
    } else if (filters?.sortOrder === 'priority') {
      query = query
        .order('priority', { ascending: false })
        .order('created_at_network', { ascending: false, nullsFirst: false });
    } else {
      query = query
        .order('created_at_network', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });
    }

    const { data, error } = await query;

    if (error) throw error;

    let results = (data || []).map((row) => this.mapToPopulatedEntity(row));

    if (filters?.excludeTicketIds && filters.excludeTicketIds.length > 0) {
      const excludeSet = new Set(filters.excludeTicketIds);
      results = results.filter((t) => !excludeSet.has(t.id));
    }

    return results;
  }

  async loadGrouped(filters?: Parameters<TicketService['loadAll']>[0]): Promise<TicketGroup[]> {
    const payload = await invokeEdgeFunction<{ data: { groups: unknown[] } }>(
      'ticket-grouped-inbox',
      {
        body: filters ?? {},
      }
    );
    const groups = payload?.data?.groups ?? [];
    return groups.map((g) => this.mapTicketGroup(g as Record<string, unknown>));
  }

  private mapTicketGroup(row: Record<string, unknown>): TicketGroup {
    const rawTickets = Array.isArray(row.tickets) ? row.tickets : [];
    const tickets: PopulatedTicket[] = rawTickets.map((t) => this.mapToPopulatedEntity(t));
    tickets.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    const firstByTime = tickets.reduce<PopulatedTicket | undefined>(
      (min, t) => (!min || new Date(t.createdAt) < new Date(min.createdAt) ? t : min),
      undefined
    );
    const lastByTime = tickets[0];

    const rawFirst = row.firstMessageAt as string | undefined;
    const rawLast = row.lastMessageAt as string | undefined;

    const head = lastByTime ?? firstByTime;

    return {
      id: (row.id as string) ?? '',
      rootTicketId: (row.rootTicketId as string | null) ?? null,
      socialUserId: (row.socialUserId as string | null) ?? null,
      socialUser: head?.socialUser,
      channel: head?.channel ?? ({} as Channel),
      tickets,
      firstMessageAt: firstByTime?.createdAt ?? rawFirst ?? '',
      lastMessageAt: lastByTime?.createdAt ?? rawLast ?? '',
      messageCount: tickets.length,
    };
  }

  async loadById(id: string): Promise<PopulatedTicket> {
    const { data, error } = await supabase
      .from('tickets')
      .select(TicketService.TICKET_SELECT)
      .eq('id', id)
      .single();

    if (error) throw error;
    return this.mapToPopulatedEntity(data);
  }

  async updateById(id: string, payload: Partial<Ticket>): Promise<PopulatedTicket> {
    const updateData: Record<string, unknown> = { updated_at: new Date().toISOString() };

    if (payload.channelId !== undefined) updateData.channel_id = payload.channelId;
    if (payload.postId !== undefined) updateData.post_id = payload.postId;
    if (payload.parentTicketId !== undefined) updateData.parent_ticket_id = payload.parentTicketId;
    if (payload.type !== undefined) updateData.type = payload.type;
    if (payload.content !== undefined) {
      updateData.content =
        typeof payload.content === 'string' ? payload.content : (payload.content.text ?? '');
    }
    if (payload.senderId !== undefined) updateData.sender_id = payload.senderId;
    if (payload.status !== undefined) updateData.status = payload.status;
    if (payload.priority !== undefined) updateData.priority = payload.priority;
    if (payload.assignedTo !== undefined) updateData.assigned_to = payload.assignedTo;
    if (payload.tags !== undefined) updateData.tags = payload.tags;
    if (payload.content?.sentiment !== undefined) updateData.sentiment = payload.content.sentiment;

    const { data, error } = await supabase
      .from('tickets')
      .update(updateData)
      .eq('id', id)
      .select(TicketService.TICKET_SELECT)
      .single();

    if (error) throw error;
    return this.mapToPopulatedEntity(data);
  }

  async findPostComments(postId: string, authorNetworkUserId?: string): Promise<PopulatedTicket[]> {
    let query = supabase
      .from('tickets')
      .select(
        '*, channels(*), users(*), orders!order_id(*), social_users:social_user_id(id, account_id, channel_id, network_id, network, full_name, username, profile_picture)'
      )
      .eq('post_id', postId)
      .eq('type', 'COMMENT')
      .eq('is_meta_ticket', false)
      .eq('is_own', false)
      .order('created_at', { ascending: false });

    if (authorNetworkUserId) {
      query = query.eq('sender_id', authorNetworkUserId);
    }

    const { data, error } = await query;

    if (error) throw error;
    return (data || []).map((row) => this.mapToPopulatedEntity(row));
  }

  async getTicketTree(
    ticketId: string
  ): Promise<(PopulatedTicket & { replies: PopulatedTicket[] })[]> {
    // Use Supabase Edge Function for unified tree building
    const response = await invokeEdgeFunction<{ data: any[] }>('ticket-tree', {
      body: { ticketId },
    });
    const tree = response?.data;
    if (!Array.isArray(tree)) return [];
    return tree.map((node: any) => this.mapCommentTreeNode(node));
  }

  async findConversationMessages(rootTicketId: string): Promise<PopulatedTicket[]> {
    const { data, error } = await supabase
      .from('tickets')
      .select(
        '*, channels(*), users(*), orders!order_id(*), social_users:social_user_id(id, account_id, channel_id, network_id, network, full_name, username, profile_picture)'
      )
      .eq('parent_ticket_id', rootTicketId)
      .eq('type', 'MESSAGE')
      .order('created_at', { ascending: true });

    if (error) throw error;
    return (data || []).map((row) => this.mapToPopulatedEntity(row));
  }

  async loadReplies(ticketId: string): Promise<TicketReply[]> {
    const { data, error } = await supabase
      .from('tickets')
      .select('*, users!left(*)')
      .eq('parent_ticket_id', ticketId)
      .eq('is_own', true)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return (data || []).map((row) => this.mapRowToTicketReply(row));
  }

  async replyToTicket(ticketId: string, message: string): Promise<TicketReply> {
    const data = await invokeEdgeFunction<{ reply: any }>('ticket-reply', {
      body: { ticketId, message },
    });
    if (!data?.reply) throw new Error('ticket-reply returned no reply data');
    return this.mapRowToTicketReply(data.reply);
  }

  private async ticketAction(
    ticketId: string,
    action: 'like' | 'unlike' | 'hide' | 'unhide' | 'delete'
  ): Promise<PopulatedTicket> {
    const data = await invokeEdgeFunction<{ ticket: any }>('ticket-action', {
      body: { ticketId, action },
    });
    return this.mapToPopulatedEntity(data.ticket);
  }

  async likeTicket(ticketId: string): Promise<PopulatedTicket> {
    return this.ticketAction(ticketId, 'like');
  }

  async unlikeTicket(ticketId: string): Promise<PopulatedTicket> {
    return this.ticketAction(ticketId, 'unlike');
  }

  async hideTicket(ticketId: string): Promise<PopulatedTicket> {
    return this.ticketAction(ticketId, 'hide');
  }

  async unhideTicket(ticketId: string): Promise<PopulatedTicket> {
    return this.ticketAction(ticketId, 'unhide');
  }

  async deleteTicket(ticketId: string): Promise<PopulatedTicket> {
    return this.ticketAction(ticketId, 'delete');
  }

  async deleteReply(replyId: string): Promise<TicketReply> {
    const data = await invokeEdgeFunction<{ reply: any }>('ticket-delete-reply', {
      body: { replyId },
    });
    if (!data?.reply) throw new Error('No reply data returned');
    return this.mapRowToTicketReply(data.reply);
  }

  async archiveTicket(ticketId: string): Promise<PopulatedTicket> {
    const { data, error } = await supabase
      .from('tickets')
      .update({
        status: 'CLOSED',
        updated_at: new Date().toISOString(),
      })
      .eq('id', ticketId)
      .select(TicketService.TICKET_SELECT)
      .single();

    if (error) throw error;
    return this.mapToPopulatedEntity(data);
  }

  async unarchiveTicket(ticketId: string): Promise<PopulatedTicket> {
    const { data, error } = await supabase
      .from('tickets')
      .update({
        status: 'OPEN',
        updated_at: new Date().toISOString(),
      })
      .eq('id', ticketId)
      .select(TicketService.TICKET_SELECT)
      .single();

    if (error) throw error;
    return this.mapToPopulatedEntity(data);
  }

  async assignTicket(ticketId: string, userId: string | null): Promise<PopulatedTicket> {
    const { data, error } = await supabase
      .from('tickets')
      .update({
        assigned_to: userId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', ticketId)
      .select(TicketService.TICKET_SELECT)
      .single();

    if (error) throw error;
    return this.mapToPopulatedEntity(data);
  }

  async generateAiReply(ticketId: string): Promise<GenerateAiReplyResponse> {
    // Use Supabase Edge Function for AI reply generation
    const data = await invokeEdgeFunction<{ data: GenerateAiReplyResponse }>('ai-reply', {
      body: { ticket_id: ticketId },
    });
    return data.data as GenerateAiReplyResponse;
  }

  async getCounts(filters?: {
    network?: string;
    type?: string;
    channelId?: string;
    startDate?: string;
    endDate?: string;
    productId?: string;
  }): Promise<TicketCountsResponse> {
    // Build base query
    let query = supabase
      .from('tickets')
      .select('status, priority, tags, assigned_to, folder_id', { count: 'exact', head: false })
      .eq('is_meta_ticket', false)
      .eq('is_own', false);

    // Apply dimension filters
    if (filters?.network && filters.network !== 'all') {
      // Need to join with channels - this is complex with Supabase
      // For now, we'll fetch all and filter in memory for network
    }
    if (filters?.type && filters.type !== 'all') {
      query = query.eq('type', filters.type);
    }
    if (filters?.channelId) {
      query = query.eq('channel_id', filters.channelId);
    }
    if (filters?.startDate) {
      query = query.gte('created_at', filters.startDate);
    }
    if (filters?.endDate) {
      query = query.lte('created_at', filters.endDate);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Count by various dimensions
    const counts: TicketCountsResponse = {
      statuses: {},
      priorities: {},
      aiTags: {},
      queues: {},
      assignments: { assigned: 0, unassigned: 0 },
    };

    (data || []).forEach((ticket) => {
      // Status counts
      const status = ticket.status || 'UNKNOWN';
      counts.statuses[status] = (counts.statuses[status] || 0) + 1;

      // Priority counts
      const priority = ticket.priority || 'NORMAL';
      counts.priorities[priority] = (counts.priorities[priority] || 0) + 1;

      // AI tags counts
      (ticket.tags || []).forEach((tag: string) => {
        counts.aiTags[tag] = (counts.aiTags[tag] || 0) + 1;
      });

      // Assignment counts
      if (ticket.assigned_to) {
        counts.assignments.assigned++;
      } else {
        counts.assignments.unassigned++;
      }

      // Queue counts (based on folder_id)
      if (ticket.folder_id) {
        // This would require looking up the folder to determine queue type
        // For now, just count by folder presence
      }
    });

    return counts;
  }

  async getFolderCounts(): Promise<{
    all: number;
    urgent: number;
    vip: number;
    buyers: number;
    myTickets: number;
    backlog: number;
    spam: number;
    archived: number;
    reminders: number;
    customFolders: Record<string, number>;
  }> {
    const data = await invokeEdgeFunction<{ data: any }>('ticket-counts', {
      method: 'GET',
    });
    return data.data;
  }

  async countTickets(filters?: { statuses?: string[]; assignedTo?: string }): Promise<number> {
    let query = supabase
      .from('tickets')
      .select('*', { count: 'exact', head: true })
      .eq('is_meta_ticket', false)
      .eq('is_own', false);

    if (filters?.statuses && filters.statuses.length > 0) {
      query = query.in('status', filters.statuses);
    } else {
      query = query.neq('status', 'CLOSED');
    }

    if (filters?.assignedTo) {
      query = query.eq('assigned_to', filters.assignedTo);
    }

    const { count, error } = await query;
    if (error) throw error;
    return count || 0;
  }

  async setRestockFollowUp(ticketId: string, productIds: string[]): Promise<PopulatedTicket> {
    const response = await fetch(
      `http://localhost/api/social-networks/inbox/tickets/${ticketId}/restock-followup`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ productIds }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to set restock follow-up');
    }
    const data = await response.json();
    return data as PopulatedTicket;
  }

  async clearRestockFollowUp(ticketId: string): Promise<PopulatedTicket> {
    const response = await fetch(
      `http://localhost/api/social-networks/inbox/tickets/${ticketId}/restock-followup`,
      {
        method: 'DELETE',
      }
    );

    if (!response.ok) {
      throw new Error('Failed to clear restock follow-up');
    }
    const data = await response.json();
    return data as PopulatedTicket;
  }

  async tagTicket(ticketId: string): Promise<{
    success: boolean;
    aiTags: string[];
    priorityScore: number;
    priority: string;
    movedToSpam: boolean;
  }> {
    // Use Supabase Edge Function for AI tagging
    return invokeEdgeFunction<{
      success: boolean;
      aiTags: string[];
      priorityScore: number;
      priority: string;
      movedToSpam: boolean;
    }>('ticket-ai-tag', {
      body: { ticketId },
    });
  }

  async analyzeSentiment(ticketId: string): Promise<AnalyzeSentimentResponse> {
    // Use Supabase Edge Function for sentiment analysis
    return invokeEdgeFunction<AnalyzeSentimentResponse>('ticket-ai-sentiment', {
      body: { ticketId },
    });
  }

  async updateSentiment(
    ticketId: string,
    sentiment: 'POSITIVE' | 'NEGATIVE' | 'NEUTRAL' | null,
    isManual: boolean = true
  ): Promise<PopulatedTicket> {
    // Use Supabase Edge Function for sentiment update
    const data = await invokeEdgeFunction('ticket-update-sentiment', {
      body: { ticketId, sentiment, isManual },
    });
    return this.mapToPopulatedEntity(data);
  }

  // Map database row to entity
  private mapToEntity(row: any): Ticket {
    // Build a proper TicketContent from the raw DB columns
    const contentText = typeof row.content === 'string' ? row.content : '';
    const content: TicketContent = {
      text: contentText,
      media: row.media_data || [],
      isLiked: row.liked ?? false,
      isHidden: row.hidden ?? false,
      isDeleted: row.deleted_on_network ?? false,
      sentiment: row.sentiment,
    };

    // Derive network from channels join if available, otherwise from network_data
    const network = row.channels?.network ?? 'FACEBOOK';

    return {
      id: row.id,
      accountId: row.account_id,
      channelId: row.channel_id,
      postId: row.post_id,
      parentTicketId: row.parent_ticket_id,
      network,
      networkTicketId: row.network_ticket_id ?? '',
      type: row.type,
      status: row.status,
      priority: row.priority,
      content,
      rootTicketId: row.root_ticket_id,
      replyCount: row.reply_count,
      lastActivityAt: row.last_activity_at,
      assignedTo: row.assigned_to,
      tags: row.tags || [],
      queue: row.queue,
      createdAtNetwork:
        row.created_at_network ?? row.network_data?.created_time ?? row.created_at ?? '',
      isImported: !!row.network_ticket_id,
      isOwn: row.is_own ?? false,
      networkData: row.network_data ?? undefined,
      deletedOnNetwork: row.deleted_on_network ?? false,
      networkStatus: row.network_status ?? undefined,
      aiTags: row.tags || [],
      aiTaggedAt: row.ai_tagged_at,
      orderId: row.order_id ?? undefined,
      socialUserId: row.social_user_id ?? row.sender_id,
      senderId: row.sender_id,
      createdAt: row.created_at ?? '',
      updatedAt: row.updated_at ?? '',
      replies: Array.isArray(row.replies) ? row.replies : [],
    };
  }

  private mapCommentTreeNode(node: any): PopulatedTicket & { replies: PopulatedTicket[] } {
    const { replies, ...row } = node;
    const mapped = this.mapToPopulatedEntity(row);
    const result = {
      ...mapped,
      replies: Array.isArray(replies)
        ? (replies as any[]).map((reply: any) => this.mapCommentTreeNode(reply))
        : [],
    } as unknown as PopulatedTicket & { replies: PopulatedTicket[] };
    return result;
  }

  private mapToPopulatedEntity(row: any): PopulatedTicket {
    const base = this.mapToEntity(row);
    const {
      channelId: _channelId,
      assignedTo: _assignedToId,
      socialUserId: _socialUserId,
      orderId: _orderId,
      postId: _postId,
      ...rest
    } = base;

    return {
      ...rest,
      channel: row.channels
        ? ({
            id: row.channels.id,
            accountId: row.channels.account_id,
            network: row.channels.network,
            name: row.channels.name,
            networkId: row.channels.network_id,
            isActive: row.channels.is_active,
            createdAt: row.channels.created_at,
            updatedAt: row.channels.updated_at,
            createdBy: {
              id: row.channels.created_by || '',
              firstName: '',
              lastName: '',
              email: '',
              phone: '',
              role: '',
              accountId: row.channels.account_id || '',
              isEnabled: true,
              createdAt: '',
              updatedAt: '',
            },
          } as unknown as Channel)
        : ({} as Channel),
      assignedTo: row.users
        ? {
            id: row.users.id,
            email: row.users.email ?? '',
            firstName: row.users.first_name ?? '',
            lastName: row.users.last_name ?? '',
            phone: row.users.phone ?? '',
            role: row.users.role ?? '',
            accountId: row.users.account_id ?? '',
            isEnabled:
              row.users.is_enabled !== undefined
                ? row.users.is_enabled
                : row.users.disabled !== undefined
                  ? !row.users.disabled
                  : true,
            createdAt: row.users.created_at ?? '',
            updatedAt: row.users.updated_at ?? '',
          }
        : undefined,
      socialUser: row.social_users
        ? {
            id: row.social_users.id,
            accountId: row.social_users.account_id,
            channelId: row.social_users.channel_id,
            networkId: row.social_users.network_id,
            network: row.social_users.network,
            fullName: row.social_users.full_name || 'Unknown',
            username: row.social_users.username,
            profilePicture: row.social_users.profile_picture,
          }
        : undefined,
      order: row.orders ? this.mapJoinedOrder(row.orders) : undefined,
      post: row.post_id ? { id: row.post_id } : null,
    };
  }

  private mapJoinedOrder(orderRow: any): Order {
    return {
      id: orderRow.id,
      accountId: orderRow.account_id,
      userId: orderRow.user_id,
      orderNumber: orderRow.order_number,
      items: orderRow.items || [],
      subtotal: parseFloat(orderRow.subtotal) || 0,
      discount: parseFloat(orderRow.discount) || 0,
      shipping: parseFloat(orderRow.shipping) || 0,
      tax: parseFloat(orderRow.tax) || 0,
      total: parseFloat(orderRow.total) || 0,
      currency: orderRow.currency || 'USD',
      couponId: orderRow.coupon_id,
      couponCode: orderRow.coupon_code,
      couponDiscount: orderRow.coupon_discount,
      status: orderRow.status,
      fulfillmentStatus: orderRow.fulfillment_status || 'UNFULFILLED',
      deliveryStatus: orderRow.delivery_status,
      deliveryProvider: orderRow.delivery_provider,
      deliveryMethod: orderRow.delivery_method,
      trackingNumber: orderRow.tracking_number,
      deliveryDate: orderRow.delivery_date,
      billingAddress: orderRow.billing_address,
      deliveryAddress: orderRow.delivery_address,
      customerEmail: orderRow.customer_email ?? '',
      customerPhone: orderRow.customer_phone,
      customerFirstName: orderRow.customer_first_name ?? '',
      customerLastName: orderRow.customer_last_name ?? '',
      customerNote: orderRow.customer_note,
      internalNote: orderRow.internal_note,
      paymentMethod: orderRow.payment_method ?? '',
      paymentStatus: orderRow.payment_status ?? 'PENDING',
      paidAt: orderRow.paid_at,
      source: orderRow.source ?? 'WEB',
      ticketId: orderRow.ticket_id,
      channelId: orderRow.channel_id,
      createdAt: orderRow.created_at,
      updatedAt: orderRow.updated_at,
      createdBy: {
        id: orderRow.created_by || '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: '',
        accountId: orderRow.account_id || '',
        isEnabled: true,
        createdAt: '',
        updatedAt: '',
      },
    };
  }

  private mapRowToTicketReply(row: any): TicketReply {
    return {
      id: row.id,
      message: row.content,
      createdAt: row.created_at,
      createdBy: row.users
        ? {
            id: row.users.id,
            email: row.users.email ?? '',
            firstName: row.users.first_name ?? '',
            lastName: row.users.last_name ?? '',
            phone: row.users.phone ?? '',
            role: row.users.role ?? '',
            accountId: row.users.account_id ?? '',
            isEnabled:
              row.users.is_enabled !== undefined
                ? row.users.is_enabled
                : row.users.disabled !== undefined
                  ? !row.users.disabled
                  : true,
            createdAt: row.users.created_at ?? '',
            updatedAt: row.users.updated_at ?? '',
          }
        : row.sender_id,
      sentToNetwork: row.status === 'SENT',
      networkReplyId: row.network_data?.networkReplyId,
      isDeleted: row.deleted_on_network ?? false,
    };
  }
}

export const ticketService = new TicketService();
